/**
 * Currencies the contract ledger can record. A tenant may operate contracts in either, but
 * every component, allocation, funding, and disbursement entry tied to one warranty is
 * denominated in that contract's single currency — the ledger does not convert or mix
 * currencies within a contract. Currency conversion is out of scope (VS-1284): a ledger entry
 * always uses the currency already established for its contract, never one converted from
 * another.
 */
export enum Currency {
  USD = 'USD',
  CAD = 'CAD',
}

/**
 * True for exactly the currencies this ledger supports. buildContractComponentSnapshot — the
 * point where a contract's currency is first established — checks this and throws
 * UnsupportedLedgerCurrencyError rather than let an unsupported value reach a ledger write;
 * every later component, allocation, funding, and disbursement entry then derives its currency
 * from that first write, so nothing downstream needs to check this again.
 */
export function isSupportedLedgerCurrency(value: unknown): value is Currency {
  return value === Currency.USD || value === Currency.CAD;
}

/**
 * Ledger amounts are decimal numbers carried to four fractional digits (e.g. 128.1234), not
 * integer cents. The extra precision — finer than USD/CAD's two-decimal minor unit — exists so
 * that proration, commission splits, and tax apportionment can flow through the ledger without
 * compounding rounding error across entries that must later sum back to a whole price.
 *
 * No component, allocation, funding, or disbursement amount is ever rounded or truncated
 * before being stored. Rounding to the currency's two-decimal minor unit happens only where
 * money actually changes hands — at the payment gateway when funding is captured, and at the
 * payout/refund rail when a disbursement executes — and it's that already-rounded, executed
 * amount that gets written to the entry, never a value the ledger re-derives or adjusts itself.
 */
export const LEDGER_AMOUNT_DECIMAL_PRECISION = 4;

/**
 * Normalizes a value computed FROM already-stored ledger amounts (e.g. contractAmountDue minus
 * netFunding) to the ledger's own decimal precision, so IEEE 754 binary floating-point noise
 * from JS arithmetic on the retrieved decimals (e.g. 1233.4501 - 833.1201 === 400.33000000000004)
 * never leaks into a derived balance response. This is NOT business rounding — every stored
 * amount already has at most LEDGER_AMOUNT_DECIMAL_PRECISION significant fractional digits, so
 * no value is actually being discarded, only representation artifacts introduced by the
 * subtraction/summation itself. Never apply this to a value before it's written to a ledger
 * entry; see the no-premature-rounding rule above.
 */
export function roundToLedgerPrecision(amount: number): number {
  const factor = 10 ** LEDGER_AMOUNT_DECIMAL_PRECISION;
  return Math.round((amount + Number.EPSILON) * factor) / factor;
}

/**
 * The ledger's signed-amount convention, by entry type — the "agreed signed-amount behavior"
 * every command handler under contract-ledger/{funding,disbursement,lifecycle,sale}/ follows
 * (VS-1284). This is business convention, not numerical hygiene (see roundToLedgerPrecision
 * above): what a positive vs. negative amount MEANS on each table, not how a value is computed.
 *
 *  - Component:    always positive. An immutable priced line item (premium, fee, tax, markup)
 *                  captured at issuance — there is no such thing as a negative price component;
 *                  a correction is a new Allocation, never a negative Component.
 *  - Allocation:   signed. Positive increases the contract's obligation (e.g. a SALE); negative
 *                  decreases it (e.g. a CANCEL's refund, a discount).
 *  - Funding:      signed. Positive is money received (a payment); negative is money that came
 *                  back (a reversal, ACH return, or chargeback) — never a refund, which is a
 *                  Disbursement (WarrantySphere's own choice to pay money out), not funding
 *                  coming back on its own.
 *  - Disbursement: signed. Positive is money paid out (a remittance, a customer refund);
 *                  negative is money recovered (a seller clawback).
 *
 * Each recordX function under those directories enforces the sign that makes it what it claims
 * to be (e.g. recordFundingReversal rejects a non-negative amount, recordPaymentFunding rejects
 * a non-positive one) rather than trusting its caller — see each function's own spec for the
 * enforced boundary. Kept as a real (if only ever read, never branched-on) value rather than a
 * plain comment so it shows up alongside Currency and LEDGER_AMOUNT_DECIMAL_PRECISION wherever
 * this module is explored, instead of living only in prose a reader has to already know to look for.
 */
export const LEDGER_SIGNED_AMOUNT_CONVENTION = {
  component:
    'always positive — an immutable priced line item; a correction is a new Allocation, never a negative Component',
  allocation: 'signed — positive increases the obligation (e.g. a SALE), negative decreases it (e.g. a CANCEL refund)',
  funding:
    'signed — positive is money received, negative is money returned (reversal/ACH return/chargeback, never a refund)',
  disbursement:
    'signed — positive is money paid out (remittance/customer refund), negative is money recovered (seller clawback)',
} as const;
